import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { nanoid } from 'nanoid';
import { Observable } from 'rxjs';
import { catchError, first, map, mergeMap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { CharonHeaders, CharonRelation } from '../../helpers/charon/charon.enum';
import { ErrorCodes } from '../../helpers/charon/error-code.enum';
import { ICharonResponseItem } from '../../models/services/charon/charon.interface';
import { IError } from '../../models/services/error/error.interface';

@Injectable({
  providedIn: 'root'
})
export class CharonBaseService {
  private charonHeaderParams: { key: CharonHeaders; value: any }[] = [];
  private charonRelation: CharonRelation;
  private correlationId: string;

  constructor(protected httpClient: HttpClient) {}

  /**
   * Realiza chamada à API atrás do módulo de comunicação do Caronte
   * @param relation Valor de referência para buscar endpoint correto dentro dos links do Caronte
   * @param headerParams Parâmetros adicionais para comunicação com o Caronte e busca das informações selecionadas.
   * @returns Observable contendo as informações finais da rota solicitada
   */
  getCharonResponse(
    relation: CharonRelation,
    method: string,
    headerParams?: { key: CharonHeaders; value: string }[],
    bodyParams?: any
  ): Observable<any> {
    this.charonRelation = relation;

    return this.getCharonEndpoint(method, headerParams).pipe(
      map((charonResponseItem: ICharonResponseItem) => {
        return {
          endpoint: charonResponseItem.href,
          updatedHeader: this.updateHeader(),
          method: charonResponseItem.method
        };
      }),
      mergeMap((data: { endpoint: string; updatedHeader: HttpHeaders; method: string }) => {
        if (data.method === 'POST') {
          return this.httpClient.post(data.endpoint, bodyParams, { headers: data.updatedHeader }).pipe(
            catchError((err) => {
              if (err.errorCode) {
                throw err;
              }
              return this.throwErrorObject(ErrorCodes.CANNOT_GET_SPECIFIC_RELATION, this.correlationId, '', err);
            })
          );
        }

        return this.httpClient.get(data.endpoint, { headers: data.updatedHeader, observe: 'response' }).pipe(
          catchError((err) => {
            if (err.errorCode) {
              throw err;
            }
            return this.throwErrorObject(ErrorCodes.CANNOT_GET_SPECIFIC_RELATION, this.correlationId, '', err);
          })
        );
      })
      // retry(2),
    );
  }

  /**
   * Inicia processo de comunicação com o Caronte para busca das rotas disponíveis dentro do entrypoint definido
   * @param headerParams Parâmetros adicionais para comunicação com o Caronte e busca das informações selecionadas.
   * @returns Observable contendo as informações finais da API
   */
  private getCharonEndpoint(method: string, headerParams?: { key: CharonHeaders; value: string }[]): Observable<any> {
    // @ts-ignore
    return window.getSharedData('token').pipe(
      first(),
      map((token: string) => {
        this.initializeCharonHeaderParams(token, headerParams);
        return { authHeader: this.updateHeader() };
      }),
      mergeMap((data: { authHeader: HttpHeaders }) => {
        return this.httpClient.get(environment.charonEntrypoint, { headers: data.authHeader, observe: 'response' }).pipe(
          map((response) => {
            if (!response?.body) {
              this.throwErrorObject(ErrorCodes.NO_RELATION, this.correlationId);
            }

            if (response.headers.get(CharonHeaders.SESSION)) {
              this.charonHeaderParams.push({
                key: CharonHeaders.SESSION,
                value: response.headers.get(CharonHeaders.SESSION)
              });
            } else {
              this.throwErrorObject(ErrorCodes.NO_CHARON_SESSION, this.correlationId);
            }

            const { body }: any = response;
            const linkList: ICharonResponseItem[] = body.links;

            let endpoint;

            for (const linkElement of linkList) {
              if (linkElement.rel === this.charonRelation && linkElement.method === method) {
                endpoint = linkElement;
                return endpoint;
              }
            }
            if (!endpoint) {
              this.throwErrorObject(ErrorCodes.NO_SPECIFIC_RELATION, this.correlationId);
            }
          }),
          catchError((err) => {
            if (err.errorCode) {
              throw err;
            }
            return this.throwErrorObject(ErrorCodes.CANNOT_GET_RELATION_LIST, this.correlationId, '', err);
          })
        );
      })
    );
  }

  /**
   * Adiciona os parâmetros iniciais necessários para conexão com o caronte
   * @param token token de segurança do usuário
   * @param headerParams Parâmetros adicionais para comunicação com o Caronte e busca das informações selecionadas.
   */
  private initializeCharonHeaderParams(token: string, headerParams?: { key: CharonHeaders; value: string }[]): void {
    if (headerParams) {
      this.charonHeaderParams = headerParams;
    } else {
      this.charonHeaderParams = [];
    }

    this.correlationId = nanoid(16);

    this.charonHeaderParams.push(
      { key: CharonHeaders.CORRELATION_ID, value: this.correlationId },
      { key: CharonHeaders.AUTHORIZATION, value: `Bearer ${token}` }
    );
  }

  /**
   * Atualiza o header para conter todos os parametros definido na variável de parâmetros do caronte
   * @returns HttpHeaders atualizado
   */
  private updateHeader(): HttpHeaders {
    let authHeader = new HttpHeaders();
    for (const param of this.charonHeaderParams) {
      authHeader = authHeader.append(param.key, param.value);
    }
    return authHeader;
  }

  throwErrorObject(errorCode: ErrorCodes, correlationID?: string, message?: string, errorObject?: any): Observable<IError> {
    const errObj: IError = {
      errorCode,
      correlationID,
      message,
      errorObject
    };
    throw errObj;
  }
}
